import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { ButtonV2 } from '../../button-v2/button-v2';
import { mergeStyling } from '../../common-functions';

const Input = ({ className, type, ...props }: React.ComponentProps<'input'>) => {
  return (
    <input
      type={type}
      data-slot="input"
      className={mergeStyling(
        'h-8 w-full min-w-0 rounded-sm border border-input bg-transparent px-2.5 py-1.5 md:py-1',
        'text-base outline-hidden placeholder:text-muted-foreground md:text-sm',
        'file:inline-flex file:h-8 file:border-0 file:bg-transparent file:font-medium file:text-foreground file:text-sm',
        'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
        'aria-invalid:border-error aria-invalid:ring-2 aria-invalid:ring-error/20',
        'aria-invalid:focus-visible:border-error aria-invalid:focus-visible:ring-error/60',
        'disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-50',
        className
      )}
      {...props}
    />
  );
};

const Textarea = ({ className, ...props }: React.ComponentProps<'textarea'>) => {
  return (
    <textarea
      data-slot="textarea"
      className={mergeStyling(
        'field-sizing-content flex min-h-16 w-full rounded-sm border border-input bg-transparent px-2.5 py-2',
        'text-base outline-hidden placeholder:text-muted-foreground md:text-sm',
        'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
        'aria-invalid:border-error aria-invalid:ring-2 aria-invalid:ring-error/20',
        'aria-invalid:focus-visible:border-error aria-invalid:focus-visible:ring-error/60',
        'disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-50',
        className
      )}
      {...props}
    />
  );
};

const InputGroup = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={mergeStyling(
        'group/input-group relative flex h-8 w-full min-w-0 items-center',
        'rounded-sm border border-input outline-none',
        'has-[[data-slot=input-group-control]:focus-visible]:border-ring',
        'has-[[data-slot=input-group-control]:focus-visible]:ring-2 has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50',
        'has-[[data-slot][aria-invalid=true]]:border-error',
        'has-[[data-slot][aria-invalid=true]]:ring-2 has-[[data-slot][aria-invalid=true]]:ring-error/20',
        'has-[[data-slot][aria-invalid=true]]:has-[[data-slot=input-group-control]:focus-visible]:ring-error/60',
        'has-disabled:bg-muted has-disabled:opacity-50',
        'in-data-[slot=combobox-content]:focus-within:border-inherit in-data-[slot=combobox-content]:focus-within:ring-0',
        'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-start]]:h-auto has-[>textarea]:h-auto',
        'has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:flex-col',
        'has-[>[data-align=inline-end]]:[&>input]:pr-1.5 has-[>[data-align=inline-start]]:[&>input]:pl-1.5',
        'has-[>[data-align=block-start]]:[&>input]:pt-3 has-[>[data-align=block-end]]:[&>input]:pb-3',
        className
      )}
      {...props}
    />
  );
};

const inputGroupAddonVariants = cva(
  [
    'flex h-auto items-center justify-center py-1.5',
    'cursor-text select-none font-medium text-muted-foreground text-sm',
    'group-data-[disabled=true]/input-group:opacity-50',
    "[&>svg:not([class*='size-'])]:size-5 md:[&>svg:not([class*='size-'])]:size-4",
  ],
  {
    variants: {
      align: {
        'inline-start': 'order-first pl-2 has-[>button]:ml-[-0.3rem] has-[>kbd]:ml-[-0.15rem]',
        'inline-end': 'order-last pr-2 has-[>button]:mr-[-0.3rem] has-[>kbd]:mr-[-0.15rem]',
        'block-start':
          'order-first w-full justify-start px-2.5 pt-2 group-has-[>input]/input-group:pt-2 [.border-b]:pb-2',
        'block-end': 'order-last w-full justify-start px-2.5 pb-2 group-has-[>input]/input-group:pb-2 [.border-t]:pt-2',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  }
);

const InputGroupAddon = ({
  className,
  align = 'inline-start',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) => {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={mergeStyling(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) {
          return;
        }
        e.currentTarget.parentElement?.querySelector('input')?.focus();
      }}
      {...props}
    />
  );
};

const InputGroupText = ({ className, ...props }: React.ComponentProps<'span'>) => {
  return (
    <span
      className={mergeStyling(
        'flex items-center gap-2',
        'text-muted-foreground text-sm',
        "[&_svg:not([class*='size-'])]:size-5 md:[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
        className
      )}
      {...props}
    />
  );
};

const InputGroupInput = ({ className, ...props }: React.ComponentProps<'input'>) => {
  return (
    <Input
      data-slot="input-group-control"
      className={mergeStyling(
        'min-w-0 flex-1 border-0 bg-transparent px-2.5',
        'text-base outline-hidden placeholder:text-muted-foreground md:text-sm',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'focus-visible:ring-0 aria-invalid:ring-0',
        className
      )}
      {...props}
    />
  );
};

const InputGroupTextarea = ({ className, ...props }: React.ComponentProps<'textarea'>) => {
  return (
    <Textarea
      data-slot="input-group-control"
      className={mergeStyling(
        'flex-1 resize-none border-0 bg-transparent px-2.5 py-2',
        'text-base outline-hidden placeholder:text-muted-foreground md:text-sm',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'focus-visible:ring-0 aria-invalid:ring-0',
        className
      )}
      {...props}
    />
  );
};

const inputGroupButtonVariants = cva('flex items-center gap-2 text-sm shadow-none', {
  variants: {
    size: {
      xs: "h-6 gap-1 rounded-[calc(var(--radius)-3px)] px-1.5 [&>svg:not([class*='size-'])]:size-3.5",
      sm: '',
      'icon-xs': 'size-6 rounded-[calc(var(--radius)-3px)] p-0 has-[>svg]:p-0',
      'icon-sm': 'size-8 p-0 has-[>svg]:p-0',
    },
  },
  defaultVariants: {
    size: 'xs',
  },
});

const InputGroupButton = ({
  className,
  type = 'button',
  variant = 'ghost',
  size = 'xs',
  ...props
}: Omit<React.ComponentProps<typeof ButtonV2>, 'size' | 'type'> &
  VariantProps<typeof inputGroupButtonVariants> & {
    type?: 'button' | 'submit' | 'reset';
  }) => {
  return (
    <ButtonV2
      type={type}
      data-slot="input-group-button"
      data-size={size}
      variant={variant}
      className={mergeStyling(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  );
};

export {
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
  Textarea,
};
